import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const BRANCH_LABEL = {
  bygg: "Bygg",
  snickeri: "Snickeri",
  vvs: "VVS",
  maleri: "Måleri",
  fastighetsservice: "Fastighetsservice",
  "mark-anlaggning": "Mark & anläggning",
  "golv-kakel": "Golv & kakel",
  el: "El",
  "fasad-tak": "Fasad & tak",
  "stad-hemservice": "Städ & hemservice",
};

export default function AdsBoostPanel({ connected }) {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  const [adAccountId, setAdAccountId] = useState("act_1019442937633760");
  const [landingBaseUrl, setLandingBaseUrl] = useState("https://app.trappkonsulten.se");
  const [dsaBeneficiary, setDsaBeneficiary] = useState("");
  const [dsaPayor, setDsaPayor] = useState("");
  const [dailyBudget, setDailyBudget] = useState(33);
  const [activateToday, setActivateToday] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const cfgRes = await axios.get(`${API_BASE}/api/ads/config`, { withCredentials: true });
      setConfig(cfgRes.data);
      if (cfgRes.data?.ad_account_id) setAdAccountId(cfgRes.data.ad_account_id);
      if (cfgRes.data?.landing_base_url) setLandingBaseUrl(cfgRes.data.landing_base_url);
      if (cfgRes.data?.dsa_beneficiary) setDsaBeneficiary(cfgRes.data.dsa_beneficiary);
      if (cfgRes.data?.dsa_payor) setDsaPayor(cfgRes.data.dsa_payor);

      if (cfgRes.data?.campaign_id) {
        const [stRes, repRes] = await Promise.all([
          axios.get(`${API_BASE}/api/ads/status`, { withCredentials: true }),
          axios.get(`${API_BASE}/api/ads/reports?days=30`, { withCredentials: true }),
        ]);
        setStatus(stRes.data);
        setReports(repRes.data?.items || []);
      } else {
        setStatus(null);
        setReports([]);
      }
    } catch (e) {
      if (e?.response?.status !== 401) toast.error("Kunde inte läsa ads-status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (connected) loadAll();
  }, [connected, loadAll]);

  const saveConfig = async () => {
    setBusy("config");
    try {
      await axios.post(
        `${API_BASE}/api/ads/config`,
        {
          ad_account_id: adAccountId.trim(),
          landing_base_url: landingBaseUrl.trim(),
          dsa_beneficiary: dsaBeneficiary.trim(),
          dsa_payor: dsaPayor.trim(),
        },
        { withCredentials: true },
      );
      toast.success("Konfiguration sparad");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte spara");
    } finally {
      setBusy("");
    }
  };

  const runBoost = async () => {
    if (!window.confirm(
      `Bygg evergreen-kampanj?\n\n` +
      `• Dagsbudget: ${dailyBudget} kr\n` +
      `• 10 ad sets (en per bransch)\n` +
      `• ${activateToday ? "Aktiveras direkt" : "Sätts PAUSED — aktivera manuellt i Ads Manager"}\n\n` +
      `Kräver att alla 10 reels är publicerade organiskt först.\n\n` +
      `Detta tar 60-120s och körs i bakgrunden — du får löpande status.`,
    )) return;
    setBusy("boost");
    let toastId;
    try {
      // 1) Starta jobbet — svarar direkt med job_id
      const startRes = await axios.post(
        `${API_BASE}/api/ads/boost`,
        { daily_budget_sek: Number(dailyBudget), activate_today: activateToday },
        { withCredentials: true, timeout: 30000 },
      );
      if (startRes.data?.already_exists) {
        toast.info("Kampanjen finns redan — inget nytt byggt");
        await loadAll();
        return;
      }
      const jobId = startRes.data?.job_id;
      if (!jobId) {
        throw new Error("Ingen job_id från servern");
      }
      toastId = toast.loading("Startar kampanj-bygget…");

      // 2) Polla var 3:e sekund tills completed/failed (max 5 min)
      const maxAttempts = 100;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((r) => setTimeout(r, 3000));
        const stRes = await axios.get(
          `${API_BASE}/api/ads/boost/status/${jobId}`,
          { withCredentials: true, timeout: 15000 },
        );
        const st = stRes.data || {};
        const msg = st.progress?.message || st.status;
        toast.loading(`${msg} (${st.progress?.current || 0}/${st.progress?.total || 11})`, { id: toastId });

        if (st.status === "completed") {
          toast.success(
            `Kampanj byggd! ${st.ad_sets?.length || 10} ad sets skapade${st.warning ? " (varning: " + st.warning + ")" : ""}`,
            { id: toastId, duration: 8000 },
          );
          await loadAll();
          return;
        }
        if (st.status === "failed") {
          toast.error(`Bygge misslyckades: ${st.error || "okänt fel"}`, { id: toastId, duration: 10000 });
          return;
        }
      }
      toast.error("Timeout — jobbet tar för lång tid. Kolla /api/ads/config för status.", { id: toastId });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Bygge misslyckades";
      if (toastId) toast.error(msg, { id: toastId });
      else toast.error(msg);
    } finally {
      setBusy("");
    }
  };

  const runRotate = async () => {
    setBusy("rotate");
    try {
      const r = await axios.post(`${API_BASE}/api/ads/rotate-daily`, {}, { withCredentials: true });
      toast.success(`Rotation klar — aktiv bransch: ${BRANCH_LABEL[r.data?.active_slug] || r.data?.active_slug}`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotation misslyckades");
    } finally {
      setBusy("");
    }
  };

  const runPauseResume = async (action) => {
    setBusy(action);
    try {
      await axios.post(`${API_BASE}/api/ads/${action}`, {}, { withCredentials: true });
      toast.success(action === "pause" ? "Kampanjen pausad" : "Kampanjen återaktiverad");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Åtgärd misslyckades");
    } finally {
      setBusy("");
    }
  };

  const runDelete = async () => {
    if (!window.confirm("Ta bort HELA kampanjen i Meta + rensa lokala kopplingar?\nDetta kan inte ångras.")) return;
    setBusy("delete");
    try {
      await axios.delete(`${API_BASE}/api/ads/campaign`, { withCredentials: true });
      toast.success("Kampanj borttagen");
      setStatus(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Borttagning misslyckades");
    } finally {
      setBusy("");
    }
  };

  const runSync = async () => {
    setBusy("sync");
    try {
      const r = await axios.post(
        `${API_BASE}/api/ads/sync-from-meta`,
        {},
        { withCredentials: true, timeout: 60000 },
      );
      toast.success(
        `Synk klar — ${r.data?.ad_sets_count} ad sets importerade (${r.data?.campaign_status})`,
        { duration: 6000 },
      );
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Synk misslyckades");
    } finally {
      setBusy("");
    }
  };

  if (!connected) {
    return (
      <div className="mt-6 border border-app rounded-lg p-4 bg-app">
        <h3 className="text-lg font-semibold mb-1">Meta Ads — Boosta som annons</h3>
        <p className="text-sm text-text-muted">Anslut Meta först ovan för att aktivera Ads.</p>
      </div>
    );
  }

  const built = !!status?.built;
  const ins = status?.insights;

  return (
    <div className="mt-6 border border-app rounded-lg p-4 bg-app" data-testid="ads-boost-panel">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-lg font-semibold">💰 Meta Ads — Boosta som annons</h3>
        {status?.campaign_status && (
          <span className={"text-xs px-2 py-0.5 rounded " + (
            status.campaign_status === "ACTIVE" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
          )} data-testid="ads-campaign-status">
            {status.campaign_status}
          </span>
        )}
      </div>

      {loading && <div className="text-sm text-text-muted">Laddar…</div>}

      {/* Config */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-sm">
          Ad Account ID
          <input
            data-testid="ads-input-account"
            className="mt-1 w-full border border-app rounded px-2 py-1 font-mono text-xs"
            value={adAccountId}
            onChange={(e) => setAdAccountId(e.target.value)}
            placeholder="act_XXXXXXXXX"
          />
        </label>
        <label className="text-sm">
          Landningssidor bas-URL
          <input
            data-testid="ads-input-landing"
            className="mt-1 w-full border border-app rounded px-2 py-1 font-mono text-xs"
            value={landingBaseUrl}
            onChange={(e) => setLandingBaseUrl(e.target.value)}
          />
        </label>
        <label className="text-sm">
          Företag som gynnas (DSA)
          <input
            data-testid="ads-input-dsa-beneficiary"
            className="mt-1 w-full border border-app rounded px-2 py-1 text-xs"
            value={dsaBeneficiary}
            onChange={(e) => setDsaBeneficiary(e.target.value)}
            placeholder="t.ex. Österlenvägen AB"
          />
          <span className="text-[10px] text-app-muted">EU DSA: företaget vars produkt marknadsförs</span>
        </label>
        <label className="text-sm">
          Företag som betalar (DSA)
          <input
            data-testid="ads-input-dsa-payor"
            className="mt-1 w-full border border-app rounded px-2 py-1 text-xs"
            value={dsaPayor}
            onChange={(e) => setDsaPayor(e.target.value)}
            placeholder="t.ex. Österlenvägen AB"
          />
          <span className="text-[10px] text-app-muted">EU DSA: företaget som betalar för annonserna</span>
        </label>
        <div className="md:col-span-2 flex gap-2">
          <button
            data-testid="ads-btn-save-config"
            onClick={saveConfig}
            disabled={busy === "config"}
            className="text-xs px-3 py-1 border border-app rounded hover:bg-white/5"
          >
            {busy === "config" ? "Sparar…" : "Spara konfiguration"}
          </button>
          {!config?.cron_secret_configured && (
            <span className="text-xs text-amber-700 self-center">
              ⚠️ Sätt ADS_CRON_SECRET i backend/.env för cron-triggning
            </span>
          )}
        </div>
      </div>

      {/* Bygg-panel — visas endast om kampanj INTE finns */}
      {!built && (
        <div className="border border-app rounded p-3 mb-4 bg-white/5">
          <div className="text-sm font-medium mb-2">Bygg evergreen-kampanj</div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <label className="text-sm">
              Dagsbudget (kr)
              <input
                data-testid="ads-input-budget"
                type="number"
                min={10}
                max={10000}
                className="mt-1 w-full border border-app rounded px-2 py-1"
                value={dailyBudget}
                onChange={(e) => setDailyBudget(e.target.value)}
              />
              <span className="text-[10px] text-text-muted">≈ {dailyBudget * 30} kr/månad</span>
            </label>
            <label className="text-sm flex items-end gap-2">
              <input
                data-testid="ads-toggle-activate"
                type="checkbox"
                checked={activateToday}
                onChange={(e) => setActivateToday(e.target.checked)}
              />
              <span>Aktivera direkt (dagens ad set)</span>
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="ads-btn-boost"
              onClick={runBoost}
              disabled={busy === "boost" || busy === "sync"}
              className="text-sm px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {busy === "boost" ? "Bygger kampanj (~1-2 min)…" : "🚀 Bygg kampanj + 10 ad sets"}
            </button>
            <button
              data-testid="ads-btn-sync"
              onClick={runSync}
              disabled={busy === "sync" || busy === "boost"}
              className="text-sm px-4 py-2 border border-blue-600 text-blue-700 rounded hover:bg-blue-50 disabled:opacity-50"
              title="Använd om kampanjen redan finns i Meta men lokal DB är tom (t.ex. efter miljöbyte)"
            >
              {busy === "sync" ? "Synkar…" : "🔄 Synka från Meta"}
            </button>
          </div>
          <div className="mt-2 text-[11px] text-text-muted">
            Tips: Om din kampanj redan kör i Meta men den här panelen är tom — klicka <strong>Synka från Meta</strong> så importeras kampanjen till lokal DB.
          </div>
        </div>
      )}

      {/* Status-widget — visas när kampanj finns */}
      {built && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Stat label="Dagsbudget" value={`${status.daily_budget_sek} kr`} />
            <Stat label="Aktiv bransch idag" value={BRANCH_LABEL[status.rotation?.today_slug] || status.rotation?.today_slug} />
            <Stat label="Dag i månad" value={`${status.rotation?.today_day} → sida ${((status.rotation?.today_day - 1) % 10) + 1}`} />
            <Stat label="Ad sets" value={`${status.ad_sets?.length || 0} st`} />
          </div>

          {status?.today_delivery && !status.today_delivery.error && (
            <LearningStageCard delivery={status.today_delivery} />
          )}

          {ins && !ins.error && (
            <div className="border border-app rounded p-3 mb-4 bg-white/5">
              <div className="text-sm font-medium mb-2">
                📊 Senaste {ins.period_days} dagar
                {typeof ins.spend_sek === "number" && (
                  <span className="ml-2 text-[11px] text-text-muted font-normal">
                    Spend: <span className="font-mono">{ins.spend_sek.toFixed(2)} kr</span>
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Visningar" value={(ins.impressions || 0).toLocaleString("sv-SE")} />
                <Stat label="Unika" value={(ins.reach || 0).toLocaleString("sv-SE")} />
                <Stat label="Klick" value={(ins.clicks || 0).toLocaleString("sv-SE")} />
                <Stat label="Konvertering" value={`${(ins.ctr_percent || 0).toFixed(2)} %`} />
              </div>
            </div>
          )}
          {ins?.error && (
            <div className="text-xs text-amber-700 mb-3">
              Insights-fel: {ins.error} (kampanjen är för ny — vänta tills första impressioner)
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-4">
            <button
              data-testid="ads-btn-rotate"
              onClick={runRotate}
              disabled={busy === "rotate"}
              className="text-sm px-3 py-1 border border-app rounded hover:bg-white/5"
            >
              {busy === "rotate" ? "Roterar…" : "🔄 Kör rotation nu"}
            </button>
            {status.campaign_status === "ACTIVE" ? (
              <button
                data-testid="ads-btn-pause"
                onClick={() => runPauseResume("pause")}
                disabled={busy === "pause"}
                className="text-sm px-3 py-1 border border-amber-600 text-amber-700 rounded hover:bg-amber-50"
              >
                ⏸ Pausa kampanj
              </button>
            ) : (
              <button
                data-testid="ads-btn-resume"
                onClick={() => runPauseResume("resume")}
                disabled={busy === "resume"}
                className="text-sm px-3 py-1 border border-green-600 text-green-700 rounded hover:bg-green-50"
              >
                ▶ Återaktivera
              </button>
            )}
            <button
              data-testid="ads-btn-sync"
              onClick={runSync}
              disabled={busy === "sync"}
              className="text-sm px-3 py-1 border border-app rounded hover:bg-white/5"
              title="Läs om kampanjstruktur från Meta (efter manuella ändringar i Ads Manager)"
            >
              {busy === "sync" ? "Synkar…" : "🔄 Synka från Meta"}
            </button>
            <button
              data-testid="ads-btn-delete"
              onClick={runDelete}
              disabled={busy === "delete"}
              className="text-sm px-3 py-1 border border-red-600 text-red-700 rounded hover:bg-red-50 ml-auto"
            >
              🗑 Ta bort hela kampanjen
            </button>
          </div>

          <details className="text-xs">
            <summary className="cursor-pointer text-text-muted hover:text-app">
              Visa alla 10 ad sets
            </summary>
            <div className="mt-2 space-y-1">
              {status.ad_sets?.map((s) => (
                <div key={s.adset_id} className="flex items-center justify-between font-mono text-[11px] border-b border-app py-1">
                  <span>
                    <strong>{BRANCH_LABEL[s.slug] || s.slug}</strong>
                    {" — "}
                    <span className="text-text-muted">
                      ålder {s.targeting_summary?.age}, {s.targeting_summary?.interests_count}i / {s.targeting_summary?.work_positions_count}r
                    </span>
                  </span>
                  <a href={s.landing_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                    landningssida ↗
                  </a>
                </div>
              ))}
            </div>
          </details>

          {/* Daglig historik */}
          <div className="mt-4 border-t border-app pt-4">
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-sm font-medium">📅 Daglig historik ({reports.length} dagar)</div>
              <div className="text-[10px] text-text-muted">
                Auto-mail till admin varje morgon • Veckosammanfattning mån 06:00
              </div>
            </div>
            {reports.length === 0 ? (
              <div className="text-xs text-text-muted p-3 bg-white/5 rounded" data-testid="ads-reports-empty">
                Ingen historik än — första rapporten skapas efter första rotationen (imorgon om cron är på).
              </div>
            ) : (
              <div className="overflow-x-auto" data-testid="ads-reports-table">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted border-b border-app">
                      <th className="py-1 pr-2">Datum</th>
                      <th className="py-1 pr-2">Bransch</th>
                      <th className="py-1 pr-2 text-right">Spend</th>
                      <th className="py-1 pr-2 text-right">Visningar</th>
                      <th className="py-1 pr-2 text-right">Unika</th>
                      <th className="py-1 pr-2 text-right">Klick</th>
                      <th className="py-1 pr-2 text-right">Konvertering</th>
                      <th className="py-1 text-right">CPC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((r) => {
                      const ins = r.insights || {};
                      return (
                        <tr key={r.date} className="border-b border-app/50 hover:bg-white/5">
                          <td className="py-1 pr-2 font-mono">{r.date}</td>
                          <td className="py-1 pr-2">{r.display_name || r.slug}</td>
                          <td className="py-1 pr-2 text-right font-mono">{(ins.spend || 0).toFixed(2)}</td>
                          <td className="py-1 pr-2 text-right font-mono">{(ins.impressions || 0).toLocaleString("sv-SE")}</td>
                          <td className="py-1 pr-2 text-right font-mono">{(ins.reach || 0).toLocaleString("sv-SE")}</td>
                          <td className="py-1 pr-2 text-right font-mono">{(ins.clicks || 0).toLocaleString("sv-SE")}</td>
                          <td className="py-1 pr-2 text-right font-mono">{(ins.ctr || 0).toFixed(2)}%</td>
                          <td className="py-1 text-right font-mono">{(ins.cpc || 0).toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-app border border-app rounded p-2">
      <div className="text-[10px] uppercase tracking-widest text-text-muted">{label}</div>
      <div className="text-sm font-mono mt-1">{value ?? "—"}</div>
    </div>
  );
}

/**
 * Visar Metas leverans-status för dagens aktiva ad set: "Lär sig",
 * "Begränsad inlärning", "Klar", eller andra statusar som "Under granskning".
 * Detta förklarar varför budgeten inte spenderas fullt ut de första 2-4 dagarna
 * (Meta samlar 50 konverteringar innan algoritmen exit:ar Learning Phase).
 */
function LearningStageCard({ delivery }) {
  const stage = delivery.learning_stage; // LEARNING / LEARNING_LIMITED / SUCCESS / null
  const eff = delivery.effective_status;   // ACTIVE / PENDING_REVIEW / WITH_ISSUES / …

  // Färg + label per status
  const info = (() => {
    if (eff === "PENDING_REVIEW" || eff === "IN_PROCESS") {
      return {
        badge: "Under Meta-granskning",
        tone: "bg-blue-50 border-blue-300 text-blue-800",
        icon: "⏳",
        title: "Meta granskar annonsen",
        body: "Meta går igenom din annons (regelverk, målgrupp, kreativ). Det tar oftast under 24 h. Så länge du ser denna status har annonsen inte börjat visas för din målgrupp — och därför spenderas ingen budget.",
      };
    }
    if (eff === "DISAPPROVED") {
      return {
        badge: "Avvisad av Meta",
        tone: "bg-red-50 border-red-300 text-red-800",
        icon: "⚠️",
        title: "Annonsen är avvisad",
        body: "Kolla Meta Ads Manager för vad som behöver ändras (oftast text, bild eller målgrupp). Efter fix körs granskningen om.",
      };
    }
    if (eff === "WITH_ISSUES") {
      return {
        badge: `Problem (${delivery.issues_count || "?"})`,
        tone: "bg-amber-50 border-amber-300 text-amber-800",
        icon: "⚠️",
        title: "Meta har rapporterat problem",
        body: "Öppna Meta Ads Manager för att se detaljerna. Kan vara målgruppsvarningar eller policybrott som behöver åtgärdas.",
      };
    }
    if (stage === "LEARNING") {
      const conv = delivery.learning_conversions ?? 0;
      return {
        badge: "Lär sig",
        tone: "bg-indigo-50 border-indigo-300 text-indigo-800",
        icon: "🎓",
        title: "Meta lär sig just nu — helt normalt",
        body: (
          <>
            Algoritmen samlar data för att hitta bästa målgruppen. Under denna fas
            spenderas budgeten <strong>ojämnt</strong> (kan vara långt under dagsbudget) och
            resultaten är opålitliga. Meta går ur inlärningen efter{" "}
            <strong>ca 50 konverteringar</strong> per vecka på ad set-nivå.
            {conv > 0 && <> Nuvarande signaler: <strong>{conv}</strong>.</>}
          </>
        ),
      };
    }
    if (stage === "LEARNING_LIMITED") {
      return {
        badge: "Begränsad inlärning",
        tone: "bg-orange-50 border-orange-300 text-orange-800",
        icon: "🚧",
        title: "För lite data för inlärning",
        body: "Ad set:et får inte nog med konverteringar/klick för att Meta ska optimera. Överväg att höja dagsbudgeten, bredda målgruppen, eller vänta tills fler ad sets ackumulerat data.",
      };
    }
    if (stage === "SUCCESS") {
      return {
        badge: "Klar med inlärning",
        tone: "bg-green-50 border-green-300 text-green-800",
        icon: "✅",
        title: "Optimerar för fullt",
        body: "Meta har lärt sig och spenderar nu budgeten där den ger bäst effekt. Insights (spend/CTR/CPC) är nu pålitliga att fatta beslut på.",
      };
    }
    if (eff === "ACTIVE") {
      return {
        badge: "Aktiv (väntar på data)",
        tone: "bg-slate-50 border-slate-300 text-slate-700",
        icon: "📡",
        title: "Annonsen är aktiv men Meta har ännu ingen leveransstatus",
        body: "Kampanjen är för ny — kolla igen om några timmar när Meta hunnit visa den för första gången.",
      };
    }
    return {
      badge: eff || "Okänd",
      tone: "bg-slate-50 border-slate-300 text-slate-700",
      icon: "•",
      title: `Status: ${eff || "okänd"}`,
      body: null,
    };
  })();

  return (
    <div
      className={`border rounded p-3 mb-4 ${info.tone}`}
      data-testid="ads-learning-stage-card"
    >
      <div className="flex items-start gap-3">
        <div className="text-2xl leading-none" aria-hidden>{info.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-medium">{info.title}</div>
            <span
              className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-semibold ${info.tone.replace("bg-", "bg-").replace("border-", "").replace("text-", "text-")}`}
              data-testid="ads-learning-stage-badge"
            >
              {info.badge}
            </span>
          </div>
          {info.body && (
            <div className="text-xs mt-1 leading-relaxed opacity-90">{info.body}</div>
          )}
          {delivery.name && (
            <div className="text-[10px] mt-2 opacity-60 font-mono">
              Ad set: {delivery.name}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
